import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { get } from 'lodash';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanAccountActivity } from '../../../interfaces';
import { SessionStoreClass } from '../../../stores';
import { FormattingUtils, SmartBool } from '../../../utils';
import { ConfirmDelete, Icon, LinkButton } from '../../common';

import LoanAccountActivity from './LoanAccountActivity';

const { formatParagraphs } = FormattingUtils;
const { PERMISSIONS } = AppConstants;

interface IProps {
  activity: ILoanAccountActivity;
  // The deleted note only reads as deleted once the feed has been re-read
  onNoteDeleted: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * A note someone on the account's team wrote, which is the one entry in the feed a person puts there
 * and the one they can take back. Deleting keeps it in the feed, greyed and stamped with the date,
 * for anyone who asks to see deleted notes. c.f. CaseNote.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class Note extends Component<IProps> {
  private showConfirmDelete = new SmartBool();

  private get injected () {
    return this.props as IInjected;
  }

  /**
   * A note is deletable by whoever wrote it and by no one else, which is what the endpoint enforces
   * too, and only by users holding the delete permission it checks.
   */
  private get isDeletable () {
    const { activity } = this.props
      , { SessionStore } = this.injected
      , authorId = get(activity, 'performed_by.id')
      ;

    return (
      !activity.deleted_at
      && !!authorId
      && authorId === get(SessionStore.user, 'id')
      && SessionStore.userHasPermission(PERMISSIONS.DELETE_LOAN_ACCOUNT_NOTE)
    );
  }

  private async onClickDelete () {
    const { activity, onNoteDeleted } = this.props
      , { Clients } = this.injected
      ;

    try {
      await Clients.loanAccountNotes.delete(activity.id);
      await onNoteDeleted();
    }
    catch (error) {
      // Transport has already toasted the failure and an onClick has nowhere to hand a rejection on
      // to, so the note is left as it was rather than the rejection going unhandled.
      // tslint:disable-next-line no-console
      console.error('Error deleting the note:', error);
    }
    finally {
      this.showConfirmDelete.setFalse();
    }
  }

  private renderActions () {
    if (!this.isDeletable) { return null; }

    return (
      <LinkButton className='btn-delete-note' onClick={this.showConfirmDelete.setTrue}>
        <Icon type='trash-o' />
      </LinkButton>
    );
  }

  public render () {
    const { activity } = this.props
      , activityBody = (
        <div>
          <h3>Note</h3>
          {formatParagraphs(activity.data.body)}
        </div>
      )
      ;

    return (
      <div>
        <LoanAccountActivity
          actions={this.renderActions()}
          activity={activity}
          activity_body={activityBody}
        />

        <ConfirmDelete
          message='Delete this note?'
          onClickCancel={this.showConfirmDelete.setFalse}
          onClickDelete={this.onClickDelete}
          showConfirmDelete={this.showConfirmDelete.isTrue}
        />
      </div>
    );
  }
}

export default Note;
